import { Zap, Shield, Eye, Thermometer, Sparkles } from "lucide-react";
import SectionDivider from "@/components/SectionDivider";
import SunRays from "@/components/SunRays";
import TiltCard from "@/components/TiltCard";
import AnimatedCounter from "@/components/AnimatedCounter";
import FloatingBlobs from "@/components/FloatingBlobs";

const benefits = [
  { icon: Zap, title: "Energy Savings", description: "Help cut cooling costs by blocking solar heat at the window.", stat: 0, statSuffix: "", statLabel: "savings" },
  { icon: Shield, title: "UV Protection", description: "Block harmful UV rays. Protect furniture and flooring.", stat: 0, statSuffix: "", statLabel: "" },
  { icon: Eye, title: "Daytime Privacy", description: "See out clearly while keeping prying eyes from looking in.", stat: 100, statSuffix: "%", statLabel: "privacy" },
  { icon: Thermometer, title: "Cooler Home", description: "Helps keep indoor temperatures more comfortable — without blocking your view.", stat: 0, statSuffix: "", statLabel: "cooler" },
  { icon: Sparkles, title: "Curb Appeal", description: "Sleek, modern look that enhances your home's appearance.", stat: 5, statSuffix: "★", statLabel: "rated" },
];

export default function BenefitsSection() {
  return (
    <>
      <section id="benefits" className="py-12 md:py-28 bg-section-warm texture-stripes texture-noise relative overflow-hidden">
        <SunRays variant="full" />
        <FloatingBlobs />

        <div className="absolute top-6 left-4 w-16 h-16 md:top-10 md:left-8 md:w-24 md:h-24 opacity-15 animate-float-slow">
          <div className="grid grid-cols-4 gap-1.5 md:gap-2">
            {Array.from({ length: 16 }).map((_, i) => (
              <div key={i} className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-primary" />
            ))}
          </div>
        </div>

        <div className="container relative z-10">
          <div className="text-center mb-8 md:mb-14">
            <span className="inline-block bg-secondary/30 text-secondary-foreground rounded-full px-4 py-1.5 font-bold text-sm mb-4 tilt-right">
              Why Sun Screens?
            </span>
            <h2 className="text-3xl md:text-5xl font-black text-foreground mb-4 text-shadow-pop">
              Benefits That <span className="text-gradient">Pay For Themselves</span>
            </h2>
            <div className="shimmer-line max-w-xs mx-auto mt-6 rounded-full hidden md:block" />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-6">
            {benefits.map((b, i) => (
              <TiltCard key={b.title} className={`glass-card rounded-2xl md:rounded-3xl p-3 md:p-6 text-center comic-border-teal card-elite gradient-border ${
                  i % 3 === 0 ? 'md:tilt-left' : i % 3 === 1 ? 'md:tilt-right' : ''
                } ${i === 4 ? 'col-span-2 lg:col-span-1' : ''}`}
              >
                <div className="w-10 h-10 md:w-14 md:h-14 rounded-xl md:rounded-2xl bg-accent flex items-center justify-center mx-auto mb-2 md:mb-3">
                  <b.icon className="w-5 h-5 md:w-7 md:h-7 text-accent-foreground" />
                </div>
                <div className="text-xl md:text-2xl font-black text-primary mb-1">
                  {b.stat > 0 ? <AnimatedCounter end={b.stat} suffix={b.statSuffix} /> : <span>✓</span>}
                </div>
                <h3 className="font-black text-foreground text-sm md:text-base mb-1 md:mb-2">{b.title}</h3>
                <p className="hidden md:block text-sm text-muted-foreground leading-relaxed">{b.description}</p>
              </TiltCard>
            ))}
          </div>
        </div>
      </section>
      <SectionDivider variant="swoosh" fillClass="fill-primary" />
    </>
  );
}
